/**
 * Media Control Handler Component
 * Adds selection and delete functionality for audio/video/iframe elements in content editor
 * Uses hover-based detection since clicks on media controls don't bubble
 */

import { useEffect } from 'react';

interface MediaControlHandlerProps {
  editorRef: React.RefObject<HTMLDivElement>;
  onContentChange: () => void;
}

export default function MediaControlHandler({ editorRef, onContentChange }: MediaControlHandlerProps) {
  useEffect(() => {
    if (!editorRef.current) return;

    // Inject styles for hover effects
    const styleId = 'media-control-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .media-container-wrapper {
          position: relative;
          display: block;
        }
        /* Inside the wrapper box — negative offsets got clipped by scrolling/overflow-hidden ancestors */
        .media-container-wrapper .media-delete-btn {
          position: absolute;
          top: 6px;
          right: 6px;
          width: 28px;
          height: 28px;
          background: #ef4444;
          border: 2px solid white;
          border-radius: 50%;
          cursor: pointer;
          display: none;
          align-items: center;
          justify-content: center;
          color: white;
          z-index: 1000;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          transition: transform 0.15s, background-color 0.15s;
        }
        .media-container-wrapper:hover .media-delete-btn,
        .media-container-wrapper.selected .media-delete-btn {
          display: flex;
        }
        .media-container-wrapper .media-delete-btn:hover {
          transform: scale(1.1);
          background-color: #dc2626;
        }
        .media-container-wrapper:hover,
        .media-container-wrapper.selected {
          outline: 2px solid #3b82f6;
          outline-offset: 4px;
          border-radius: 8px;
        }
      `;
      document.head.appendChild(style);
    }

    // Process all media elements in the editor
    const processMediaElements = () => {
      const editor = editorRef.current;
      if (!editor) return;

      // Find all audio, video, and iframe elements
      const mediaElements = editor.querySelectorAll('audio, video, iframe');

      mediaElements.forEach((media) => {
        // Find the parent container div
        let container = media.closest('div[style*="margin"]') as HTMLElement;
        if (!container) {
          container = media.parentElement as HTMLElement;
        }
        if (!container || container === editor) return;

        // Skip if already processed
        if (container.classList.contains('media-container-wrapper')) return;

        // Add wrapper class
        container.classList.add('media-container-wrapper');

        // Check if delete button already exists
        if (container.querySelector('.media-delete-btn')) return;

        // Create delete button
        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'media-delete-btn';
        deleteBtn.type = 'button';
        deleteBtn.title = 'Delete';
        deleteBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        `;

        deleteBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          container.remove();
          onContentChange();
        });

        // Prevent button from triggering contenteditable issues
        deleteBtn.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });

        container.appendChild(deleteBtn);
      });
    };

    // Run initially
    processMediaElements();

    // Also run on mutations (when new media is added)
    const observer = new MutationObserver(() => {
      processMediaElements();
    });

    observer.observe(editorRef.current, {
      childList: true,
      subtree: true,
    });

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [editorRef, onContentChange]);

  return null;
}
